/**
 * NotificationBanner — top-of-screen in-app banner shown when a push notification
 * arrives while the app is in the foreground (iOS does not display the OS banner
 * in that case).
 *
 * Triggered imperatively via `showInAppBanner({title, body, data})` from anywhere.
 * Auto-dismisses after 5s. Tapping it routes to the relevant tournament/pickup.
 */

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

export type BannerPayload = {
  title: string;
  body: string;
  data?: Record<string, string | undefined>;
};

let externalShow: ((b: BannerPayload) => void) | null = null;

/** Call from anywhere (e.g. push.ts notificationReceived listener). */
export function showInAppBanner(b: BannerPayload): void {
  if (externalShow) {
    externalShow(b);
  } else {
    // BannerHost not mounted yet — silently drop (race during boot).
    console.warn("[banner] host not mounted, dropping:", b.title);
  }
}

/** Mount this once near the top of the app tree (inside Router). */
export default function NotificationBannerHost() {
  const navigate = useNavigate();
  const [banner, setBanner] = useState<BannerPayload | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    externalShow = (b) => {
      setBanner(b);
      // Trigger CSS transition by toggling visible after mount.
      setTimeout(() => setVisible(true), 20);
    };
    return () => {
      externalShow = null;
    };
  }, []);

  useEffect(() => {
    if (!banner) return;
    const t = setTimeout(() => dismiss(), 5000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [banner]);

  function dismiss() {
    setVisible(false);
    // Wait for fade-out animation before unmounting.
    setTimeout(() => setBanner(null), 250);
  }

  function handleTap() {
    if (!banner) return;
    const data = banner.data ?? {};
    if (data.type === "tournament_join" && data.tournamentId) {
      navigate(`/tournament/${data.tournamentId}`);
    } else if (data.type === "tournament_bracket_ready" && data.tournamentId) {
      navigate(`/tournament-bracket?tournamentId=${data.tournamentId}`);
    } else if (data.type === "pickup_join" && data.pickupId) {
      navigate(`/pickup/${data.pickupId}`);
    } else if (data.type?.startsWith("hit_order_") && data.orderId) {
      navigate(`/roundup/order/${data.orderId}`);
    }
    dismiss();
  }

  if (!banner) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        padding: "calc(env(safe-area-inset-top, 0px) + 8px) 12px 0",
        pointerEvents: "none",
      }}
    >
      <div
        onClick={handleTap}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter") handleTap();
        }}
        style={{
          pointerEvents: "auto",
          background: "white",
          borderRadius: 16,
          padding: "12px 14px",
          boxShadow: "0 12px 32px 0 rgba(58, 95, 148, 0.20)",
          border: "1px solid rgba(58, 95, 148, 0.12)",
          maxWidth: 440,
          margin: "0 auto",
          display: "flex",
          alignItems: "center",
          gap: 12,
          cursor: "pointer",
          transform: visible ? "translateY(0)" : "translateY(-110%)",
          opacity: visible ? 1 : 0,
          transition: "transform 250ms cubic-bezier(0.16, 1, 0.3, 1), opacity 200ms",
        }}
      >
        <div
          style={{
            width: 40,
            height: 40,
            background: "#dfff00",
            borderRadius: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="ri-notification-3-line" style={{ fontSize: 20, color: "#1f477b" }}></i>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: "#1f477b", marginBottom: 2 }}>
            {banner.title}
          </div>
          <div
            style={{
              fontSize: 13,
              color: "#454932",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
            }}
          >
            {banner.body}
          </div>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            dismiss();
          }}
          style={{
            border: "none",
            background: "transparent",
            color: "#64748b",
            fontSize: 18,
            cursor: "pointer",
            padding: 4,
            flexShrink: 0,
          }}
          aria-label="Dismiss notification"
        >
          <i className="ri-close-line"></i>
        </button>
      </div>
    </div>
  );
}
